import { useEffect, useState } from "react";
import { Camera, Download, Trash2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { fetchScanManifest } from "../lib/api";
import { packAvailable, localManifest, downloadPack, deletePack, packSize } from "../lib/scanner/pack";
import { SCAN_LOCAL_PREF } from "./ScannerCapture";

const mo = (n) => `${Math.round(n / 1e6)} Mo`;

// Réglages > Scanner : le scanner reconnaît les cartes en comparant la photo
// aux images de toutes les cartes connues. En ligne, c'est le serveur qui
// calcule ; pour scanner sans réseau, l'appli télécharge une fois le « pack »
// (modèles + index) et fait le même calcul sur le téléphone.
export const ScannerSettings = ({ isOpen }) => {
  const native = packAvailable();
  const [local, setLocal] = useState(null);
  const [remote, setRemote] = useState(null);
  const [progress, setProgress] = useState(null);
  const [msg, setMsg] = useState(null);
  const [preferLocal, setPreferLocal] = useState(() => { try { return localStorage.getItem(SCAN_LOCAL_PREF) === "1"; } catch (e) { return false; } });

  useEffect(() => {
    if (!isOpen) return;
    if (native) localManifest().then(setLocal);
    fetchScanManifest().then(setRemote).catch(() => setRemote(null));
  }, [isOpen, native]);

  const outdated = local && remote && (local.version !== remote.version ||
    Object.keys(remote.models).some((k) => remote.models[k].file !== local.models?.[k]?.file) ||
    remote.runtime?.file !== local.runtime?.file);

  const runDownload = async () => {
    setMsg(null); setProgress(0);
    try {
      setLocal(await downloadPack(setProgress));
      setMsg({ text: "Scanner hors ligne prêt." });
    } catch (e) {
      setMsg({ error: true, text: e?.message || String(e) });
    } finally { setProgress(null); }
  };

  const runDelete = async () => {
    await deletePack(); setLocal(null); setMsg({ text: "Scanner hors ligne supprimé du téléphone." });
  };

  const togglePrefer = () => {
    const v = !preferLocal; setPreferLocal(v);
    try { localStorage.setItem(SCAN_LOCAL_PREF, v ? "1" : "0"); } catch (e) { /* stockage indisponible */ }
  };

  return (
    <div className="mt-7">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Scanner</h3>
      <p className="text-sm text-zinc-400 mb-3">
        Le scanner compare ta photo aux images de toutes les cartes connues
        {remote ? ` (${remote.count.toLocaleString("fr-FR")} cartes)` : ""}, sans service
        extérieur. Il reconnaît plusieurs cartes sur une même photo. Quand il hésite,
        « Ce n'est pas ça » propose les cartes suivantes.
      </p>
      {native ? (
        <>
          <div className="flex items-center gap-2 mb-2 text-sm">
            <Camera className="w-4 h-4 text-zinc-500" />
            {local
              ? <span className={outdated ? "text-amber-300" : "text-emerald-400"}>
                  Hors ligne : installé ({local.count.toLocaleString("fr-FR")} cartes){outdated ? " — mise à jour disponible" : ""}
                </span>
              : <span className="text-zinc-500">Hors ligne : non installé (le scanner a alors besoin du réseau).</span>}
          </div>
          {progress !== null ? (
            <div className="max-w-xs">
              <Progress value={Math.round(progress * 100)} className="h-2" />
              <p className="text-xs text-zinc-400 mt-1">Téléchargement… {Math.round(progress * 100)} % — garde l'appli ouverte.</p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {(!local || outdated) && remote && (
                <Button onClick={runDownload} className="bg-sky-600 hover:bg-sky-500 text-white">
                  {local ? <RefreshCw className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  {local ? "Mettre à jour" : `Télécharger (${mo(packSize(remote))})`}
                </Button>
              )}
              {local && (
                <Button onClick={runDelete} variant="ghost" className="text-zinc-400 hover:text-white">
                  <Trash2 className="w-4 h-4 mr-2" />Supprimer
                </Button>
              )}
            </div>
          )}
          {local && (
            <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={preferLocal} onChange={togglePrefer} className="accent-sky-500" />
              Reconnaître sur le téléphone même avec du réseau
            </label>
          )}
          <p className="text-xs text-zinc-500 mt-2">
            Wi-Fi conseillé. Les nouvelles cartes sont ajoutées à l'index chaque nuit : « Mettre à jour » apparaît alors ici.
          </p>
        </>
      ) : (
        <p className="text-sm text-zinc-500">Sur le site, la reconnaissance passe par le serveur : ouvre « Scanner des cartes » dans le menu de ton avatar et choisis une photo.</p>
      )}
      {msg && <p className={`text-sm mt-2 ${msg.error ? "text-red-400" : "text-emerald-400"}`}>{msg.text}</p>}
    </div>
  );
};
